import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FeatherPlus } from "@subframe/core";
import { Button } from "../../../../ui/components/Button";
import { DateRangeFilter } from "../../../../shared/presentation/components/DateRangeFilter";
import {
  DateRangeValue,
  formatDateForApi,
} from "../../../../shared/presentation/utils/dateRangeFilters";
import { usePersistedDateRange } from "../../../../shared/presentation/hooks/usePersistedDateRange";
import { useMinimumLoading } from "../../../../shared/presentation/hooks/useMinimumLoading";
import { usePersistedSort } from "../../../../shared/presentation/hooks/usePersistedSort";
import { usePersistedState } from "../../../../shared/presentation/hooks/usePersistedState";
import {
  CreditNoteDocumentType,
  CreditNoteEntity,
  CreditNoteListStatus,
} from "../../domain";
import { useCreditNoteCounts } from "../hooks/useCreditNoteCounts";
import { useCreditNoteList } from "../hooks/useCreditNoteList";
import { CreditNoteListToolbar } from "./CreditNoteListToolbar";
import { CreditNoteStatsCards } from "./CreditNoteStatsCards";
import { CreditNoteTable } from "./CreditNoteTable";

const ITEMS_PER_PAGE = 10;
const CREDIT_NOTE_LIST_STATUSES: CreditNoteListStatus[] = [
  "all",
  "pending",
  "under_review",
  "posted",
  "rejected",
];
const CREDIT_NOTE_DOCUMENT_TYPES: CreditNoteDocumentType[] = [
  "all",
  "invoice",
  "bill",
];
type ListLoadingReason = "initial" | "pagination" | "refresh" | null;

const deserializeCreditNoteStatus = (
  raw: string,
  fallback: CreditNoteListStatus,
) => {
  const parsed = JSON.parse(raw);
  return typeof parsed === "string" &&
    CREDIT_NOTE_LIST_STATUSES.includes(parsed as CreditNoteListStatus)
    ? (parsed as CreditNoteListStatus)
    : fallback;
};

const deserializeCreditNoteDocumentType = (
  raw: string,
  fallback: CreditNoteDocumentType,
) => {
  const parsed = JSON.parse(raw);
  return typeof parsed === "string" &&
    CREDIT_NOTE_DOCUMENT_TYPES.includes(parsed as CreditNoteDocumentType)
    ? (parsed as CreditNoteDocumentType)
    : fallback;
};

const CreditNotesSection: React.FC = () => {
  const navigate = useNavigate();
  const [status, setStatus] = usePersistedState<CreditNoteListStatus>(
    "creditNotes.list.status",
    "all",
    { deserialize: deserializeCreditNoteStatus },
  );
  const [documentType, setDocumentType] =
    usePersistedState<CreditNoteDocumentType>(
      "creditNotes.list.documentType",
      "all",
      { deserialize: deserializeCreditNoteDocumentType },
    );
  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [dateRange, setDateRange] = usePersistedDateRange(
    "creditNotes.list.dateRange",
  );
  const [sort, setSort] = usePersistedSort("creditNotes.list", {
    by: "created_at",
    order: "desc",
    label: "Created Date: Newest First",
  });
  const [listLoadingReason, setListLoadingReason] =
    useState<ListLoadingReason>("initial");
  const listLoadingStartedRef = useRef(false);

  const { creditNotes, pagination, loading, error, fetchCreditNotes } =
    useCreditNoteList();

  const countParams = useMemo(
    () => ({
      date_from:
        dateRange.preset === "all_time"
          ? undefined
          : formatDateForApi(dateRange.from),
      date_to:
        dateRange.preset === "all_time"
          ? undefined
          : formatDateForApi(dateRange.to),
      document_type: documentType,
    }),
    [dateRange.from, dateRange.preset, dateRange.to, documentType],
  );
  const {
    counts: creditNoteCounts,
    loading: countsLoading,
    fetchCounts,
  } = useCreditNoteCounts(countParams);

  const listParams = useMemo(
    () => ({
      page,
      limit: ITEMS_PER_PAGE,
      status,
      search: searchTerm,
      dateFrom:
        dateRange.preset === "all_time"
          ? undefined
          : formatDateForApi(dateRange.from),
      dateTo:
        dateRange.preset === "all_time"
          ? undefined
          : formatDateForApi(dateRange.to),
      sortBy: sort.by as "created_at" | "updated_at",
      sortOrder: sort.order as "asc" | "desc",
      documentType,
    }),
    [
      dateRange.from,
      dateRange.preset,
      dateRange.to,
      documentType,
      page,
      searchTerm,
      sort.by,
      sort.order,
      status,
    ],
  );

  useEffect(() => {
    const timeoutId = window.setTimeout(
      () => {
        fetchCreditNotes(listParams);
      },
      searchTerm.trim() ? 500 : 0,
    );

    return () => window.clearTimeout(timeoutId);
  }, [fetchCreditNotes, listParams, searchTerm]);

  useEffect(() => {
    if (loading) {
      listLoadingStartedRef.current = true;
      return;
    }

    if (listLoadingStartedRef.current) {
      listLoadingStartedRef.current = false;
      setListLoadingReason(null);
    }
  }, [loading]);

  const handleStatusChange = (nextStatus: CreditNoteListStatus) => {
    if (nextStatus === status) return;

    setListLoadingReason("refresh");
    setStatus(nextStatus);
    setPage(1);
  };

  const handleDocumentTypeChange = (
    nextDocumentType: CreditNoteDocumentType,
  ) => {
    if (nextDocumentType === documentType) return;

    setListLoadingReason("refresh");
    setDocumentType(nextDocumentType);
    setPage(1);
  };

  const handleDateRangeChange = (nextDateRange: DateRangeValue) => {
    if (
      nextDateRange.preset === dateRange.preset &&
      nextDateRange.from?.getTime() === dateRange.from?.getTime() &&
      nextDateRange.to?.getTime() === dateRange.to?.getTime()
    ) {
      return;
    }

    setListLoadingReason("refresh");
    setDateRange(nextDateRange);
    setPage(1);
  };

  const handleSearchTermChange = (value: string) => {
    if (value === searchTerm) return;

    setListLoadingReason("refresh");
    setSearchTerm(value);
    setPage(1);
  };

  const handleSortChange = (
    sortBy: string,
    sortOrder: string,
    label: string,
  ) => {
    if (sortBy === sort.by && sortOrder === sort.order && label === sort.label) {
      return;
    }

    setListLoadingReason("refresh");
    setSort({ by: sortBy, order: sortOrder, label });
    setPage(1);
  };

  const handleRefresh = useCallback(() => {
    setListLoadingReason("refresh");
    fetchCreditNotes(listParams);
    fetchCounts(countParams);
  }, [countParams, fetchCounts, fetchCreditNotes, listParams]);

  const handlePageChange = (nextPage: number) => {
    if (nextPage === page) return;

    setListLoadingReason("pagination");
    setPage(nextPage);
  };

  const handleCreateManual = () => {
    navigate("/credit/review/new", {
      state: { isManualCreation: true },
    });
  };

  const handleReviewClick = (document: CreditNoteEntity) => {
    navigate(`/credit/review/${document.id}`, {
      state: {
        creditNoteJson: document.json,
        documentId: document.id.toString(),
        creditNoteDocument: document,
        signedUrl: document.signed_url,
        isEdit:
          document.status === "1" || String(document.is_under_review) === "1",
        jobId: document.jobid,
      },
    });
  };

  const showTableSkeleton =
    (listLoadingReason === "initial" && !pagination) ||
    listLoadingReason === "pagination" ||
    (loading && !pagination);
  const showInlineLoading =
    !showTableSkeleton && (listLoadingReason === "refresh" || loading);
  const smoothTableSkeleton = useMinimumLoading(showTableSkeleton);
  const smoothInlineLoading = useMinimumLoading(showInlineLoading);

  return (
    <div className="flex w-full grow shrink-0 basis-0 flex-col items-start gap-8 bg-default-background px-8 pt-8 pb-40">
      <div className="flex w-full items-center justify-between">
        <div className="flex flex-col items-start gap-2">
          <span className="text-heading-2 font-heading-2 text-default-font">
            Credit Notes
          </span>
          <span className="text-body font-body text-subtext-color">
            Track and manage your credit note workflow
          </span>
        </div>
        <Button
          variant="brand-primary"
          icon={<FeatherPlus />}
          onClick={handleCreateManual}
        >
          Create Credit Note
        </Button>
      </div>

      <DateRangeFilter value={dateRange} onChange={handleDateRangeChange} />

      <CreditNoteStatsCards
        counts={creditNoteCounts}
        loading={countsLoading}
        onSelectStatus={handleStatusChange}
      />

      <CreditNoteListToolbar
        status={status}
        onStatusChange={handleStatusChange}
        documentType={documentType}
        onDocumentTypeChange={handleDocumentTypeChange}
        searchTerm={searchTerm}
        onSearchTermChange={handleSearchTermChange}
        sort={sort}
        onSortChange={handleSortChange}
        onRefresh={handleRefresh}
        loading={loading}
      />

      <CreditNoteTable
        creditNotes={creditNotes}
        loading={smoothTableSkeleton}
        inlineLoading={smoothInlineLoading}
        error={error}
        pagination={pagination}
        itemsPerPage={ITEMS_PER_PAGE}
        searchTerm={searchTerm}
        onReview={handleReviewClick}
        onPageChange={handlePageChange}
      />
    </div>
  );
};

export default CreditNotesSection;
